import type { ZodSchema, z } from 'zod';

import { schemas } from './__generated_api.ts';

/**
 * This file contains the API schema definitions using Zod. Always import the schemas from here and never from the generated API file directly.
 * This is used to extend the schemas with additional properties or to narrow down types (like x-extensible-enums)
 * the satisfies Record<keyof typeof schemas, ZodSchema> only allows to override existing schemas (prevent accidental creation of new schemas)
 */
export const apiSchema = {
    ...schemas,
} satisfies Record<keyof typeof schemas, ZodSchema>;

export type ApiComments = z.infer<typeof apiSchema.Comments>;
export type ApiChargingDetails = z.infer<typeof apiSchema.ChargingDetails>;
export type ApiLocation = z.infer<typeof apiSchema.Location>;
export type ApiStopType = z.infer<typeof apiSchema.StopType>;
export type ApiCoordinates = z.infer<typeof apiSchema.Coordinates>;
export type ApiBaseStop = z.infer<typeof apiSchema.BaseStop>;
export type ApiBaseMaterial = z.infer<typeof apiSchema.BaseMaterial>;
export type ApiPackagingType = z.infer<typeof apiSchema.PackagingType>;
export type ApiCargoMaterialUnit = z.infer<typeof apiSchema.CargoMaterialUnit>;
export type ApiCargoMaterial = z.infer<typeof apiSchema.CargoMaterial>;
export type ApiBulkMaterialType = z.infer<typeof apiSchema.BulkMaterialType>;
export type ApiBulkMaterialUnit = z.infer<typeof apiSchema.BulkMaterialUnit>;
export type ApiBulkMaterial = z.infer<typeof apiSchema.BulkMaterial>;
export type ApiMaterials = z.infer<typeof apiSchema.Materials>;
export type ApiOrderType = z.infer<typeof apiSchema.OrderType>;
export type ApiTransportOrder = z.infer<typeof apiSchema.TransportOrder>;
export type ApiServiceOrder = z.infer<typeof apiSchema.ServiceOrder>;
export type ApiOrder = ApiTransportOrder | ApiServiceOrder;
export type ApiWriteStop = z.infer<typeof apiSchema.WriteStop>;
export type ApiUpdateStop = z.infer<typeof apiSchema.UpdateStop>;
export type ApiWriteCargoMaterialUnit = z.infer<typeof apiSchema.WriteCargoMaterialUnit>;
export type ApiWriteCargoMaterial = z.infer<typeof apiSchema.WriteCargoMaterial>;
export type ApiWriteBulkMaterialUnit = z.infer<typeof apiSchema.WriteBulkMaterialUnit>;
export type ApiWriteBulkMaterial = z.infer<typeof apiSchema.WriteBulkMaterial>;
export type ApiWriteMaterial = z.infer<typeof apiSchema.WriteMaterial>;
export type ApiWriteTransportOrder = z.infer<typeof apiSchema.WriteTransportOrder>;
export type ApiWriteServiceOrder = z.infer<typeof apiSchema.WriteServiceOrder>;
export type ApiWriteOrder = ApiWriteTransportOrder | ApiWriteServiceOrder;
export type ApiDocumentAssociation = z.infer<typeof apiSchema.DocumentAssociation>;
export type ApiDocument = z.infer<typeof apiSchema.Document>;
export type ApiDocuments = z.infer<typeof apiSchema.Documents>;
export type ApiDocumentList = z.infer<typeof apiSchema.DocumentList>;
export type ApiWriteDocument = z.infer<typeof apiSchema.WriteDocument>;
export type ApiTourCapacity = z.infer<typeof apiSchema.TourCapacity>;
export type ApiTourProgress = z.infer<typeof apiSchema.TourProgress>;
export type ApiTourProblem = z.infer<typeof apiSchema.TourProblem>;
export type ApiBaseTour = z.infer<typeof apiSchema.BaseTour>;
export type ApiTourListEntry = z.infer<typeof apiSchema.TourListEntry>;
export type ApiCreateTourStopReference = z.infer<typeof apiSchema.CreateTourStopReference>;
export type ApiWriteTour = z.infer<typeof apiSchema.WriteTour>;
export type ApiDetailedStop = z.infer<typeof apiSchema.DetailedStop>;
export type ApiTour = z.infer<typeof apiSchema.Tour>;
export type ApiDriver = z.infer<typeof apiSchema.Driver>;
export type ApiVehicle = z.infer<typeof apiSchema.Vehicle>;
export type ApiVehicleAvailability = z.infer<typeof apiSchema.VehicleAvailability>;
export type ApiVehicleAvailabilityTour = z.infer<typeof apiSchema.VehicleAvailabilityTour>;
export type ApiDuplicateTourStopReference = z.infer<typeof apiSchema.DuplicateTourStopReference>;
export type ApiDuplicateTour = z.infer<typeof apiSchema.DuplicateTour>;
export type ApiDuplicateOrder = z.infer<typeof apiSchema.DuplicateOrder>;
export type ApiWriteTourDuplicate = z.infer<typeof apiSchema.WriteTourDuplicate>;
